// Double-entry posting helpers.
// Each "post" function builds a journal + journal_lines pair that ALWAYS
// balances (sum of debits = sum of credits) and inserts them inside a
// single round-trip so a partial failure leaves the ledger consistent.

import { supabase } from './supabase';
import type { Account, Invoice, InvoiceLine, Bill, BillLine, TaxCode, BankAccount } from './types';

interface JournalLineInput {
  account_id: string;
  description?: string | null;
  debit: number;
  credit: number;
  contact_id?: string | null;
  tax_code_id?: string | null;
}

interface PostJournalInput {
  org_id: string;
  date: string;
  reference?: string | null;
  description?: string | null;
  source: 'manual' | 'invoice' | 'bill' | 'payment' | 'bank' | 'opening' | 'adjustment';
  source_id?: string | null;
  lines: JournalLineInput[];
}

/** Round to cents to avoid drift. */
export function r2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Verify debit/credit balance with cent tolerance. */
export function isBalanced(lines: JournalLineInput[]): boolean {
  const d = lines.reduce((s, l) => s + r2(l.debit ?? 0), 0);
  const c = lines.reduce((s, l) => s + r2(l.credit ?? 0), 0);
  return Math.abs(d - c) < 0.005;
}

export async function postJournal(input: PostJournalInput): Promise<{ journal_id: string | null; error: string | null }> {
  if (!isBalanced(input.lines)) {
    return { journal_id: null, error: 'Journal does not balance — debits ≠ credits.' };
  }
  const { data: j, error: je } = await supabase
    .from('dodobooks_journals')
    .insert({
      org_id: input.org_id,
      date: input.date,
      reference: input.reference ?? null,
      description: input.description ?? null,
      source: input.source,
      source_id: input.source_id ?? null,
      is_posted: true,
    })
    .select('id')
    .single();
  if (je || !j) return { journal_id: null, error: je?.message ?? 'Failed to create journal header' };

  const lines = input.lines
    .filter((l) => r2(l.debit) !== 0 || r2(l.credit) !== 0)
    .map((l) => ({
      journal_id: j.id,
      account_id: l.account_id,
      description: l.description ?? input.description ?? null,
      debit: r2(l.debit ?? 0),
      credit: r2(l.credit ?? 0),
      contact_id: l.contact_id ?? null,
      tax_code_id: l.tax_code_id ?? null,
    }));
  const { error: le } = await supabase.from('dodobooks_journal_lines').insert(lines);
  if (le) {
    // Best-effort rollback of the header.
    await supabase.from('dodobooks_journals').delete().eq('id', j.id);
    return { journal_id: null, error: le.message };
  }
  return { journal_id: j.id, error: null };
}

interface LineCalc {
  line_total: number;     // line subtotal (ex-VAT)
  line_tax: number;       // VAT amount on this line
  account_id: string | null;
  tax_code_id: string | null;
}

export function calcLine(qty: number, unitPrice: number, taxRate: number): { net: number; tax: number; total: number } {
  const net = r2(qty * unitPrice);
  const tax = r2(net * taxRate);
  return { net, tax, total: r2(net + tax) };
}

/**
 * Posting rules — sales invoice approved (status -> 'sent'):
 *   DR  1200 Accounts receivable  (gross total)
 *     CR  4xxx revenue account    (net per line)
 *     CR  2100 VAT output         (sum of line tax, if VAT registered)
 */
export async function postInvoice(opts: {
  org_id: string;
  invoice: Invoice;
  lines: InvoiceLine[];
  accounts: Account[];
  taxCodes: TaxCode[];
}): Promise<{ journal_id: string | null; error: string | null }> {
  const { org_id, invoice, lines, accounts, taxCodes } = opts;
  const ar = accounts.find((a) => a.code === '1200');
  const vatOut = accounts.find((a) => a.sars_role === 'vat_output');
  if (!ar) return { journal_id: null, error: 'Missing AR account (1200).' };

  const lineCalcs: LineCalc[] = lines.map((l) => {
    const tc = taxCodes.find((t) => t.id === l.tax_code_id);
    const rate = tc?.rate ?? 0;
    const net = r2(l.qty * l.unit_price);
    return {
      line_total: net,
      line_tax: r2(net * rate),
      account_id: l.account_id,
      tax_code_id: l.tax_code_id,
    };
  });

  const taxTotal = r2(lineCalcs.reduce((s, l) => s + l.line_tax, 0));
  const subtotal = r2(lineCalcs.reduce((s, l) => s + l.line_total, 0));
  const total = r2(subtotal + taxTotal);

  // Build journal lines: AR debit + per-account revenue credits + VAT output credit.
  const journalLines: JournalLineInput[] = [
    {
      account_id: ar.id,
      debit: total,
      credit: 0,
      contact_id: invoice.contact_id,
      description: `Invoice ${invoice.number}`,
    },
  ];
  // Group revenue credits by account.
  const revBy: Record<string, number> = {};
  for (const lc of lineCalcs) {
    const accId = lc.account_id ?? accounts.find((a) => a.code === '4000')?.id;
    if (!accId) return { journal_id: null, error: 'Invoice line has no revenue account and 4000 is missing.' };
    revBy[accId] = r2((revBy[accId] ?? 0) + lc.line_total);
  }
  for (const [accId, amt] of Object.entries(revBy)) {
    journalLines.push({
      account_id: accId,
      debit: 0,
      credit: amt,
      contact_id: invoice.contact_id,
    });
  }
  if (taxTotal > 0) {
    if (!vatOut) return { journal_id: null, error: 'VAT output account missing — chart of accounts may be incomplete.' };
    journalLines.push({
      account_id: vatOut.id,
      debit: 0,
      credit: taxTotal,
      contact_id: invoice.contact_id,
      description: 'VAT output',
    });
  }

  // Persist computed totals on the invoice + link the journal.
  const result = await postJournal({
    org_id,
    date: invoice.issue_date,
    reference: invoice.number,
    description: `Sales invoice ${invoice.number}`,
    source: 'invoice',
    source_id: invoice.id,
    lines: journalLines,
  });
  if (result.error || !result.journal_id) return result;

  const { error: ue } = await supabase
    .from('dodobooks_invoices')
    .update({
      subtotal,
      tax_total: taxTotal,
      total,
      status: 'sent',
      journal_id: result.journal_id,
    })
    .eq('id', invoice.id);
  if (ue) return { journal_id: null, error: ue.message };

  return result;
}

/**
 * Posting rules — supplier bill approved (status -> 'approved'):
 *   DR  expense / asset account   (net per line)
 *   DR  1400 VAT input            (sum of line tax, if VAT registered)
 *     CR  2000 Accounts payable   (gross total)
 */
export async function postBill(opts: {
  org_id: string;
  bill: Bill;
  lines: BillLine[];
  accounts: Account[];
  taxCodes: TaxCode[];
}): Promise<{ journal_id: string | null; error: string | null }> {
  const { org_id, bill, lines, accounts, taxCodes } = opts;
  const ap = accounts.find((a) => a.code === '2000');
  const vatIn = accounts.find((a) => a.sars_role === 'vat_input');
  if (!ap) return { journal_id: null, error: 'Missing AP account (2000).' };

  const lineCalcs: LineCalc[] = lines.map((l) => {
    const tc = taxCodes.find((t) => t.id === l.tax_code_id);
    const rate = tc?.rate ?? 0;
    const net = r2(l.qty * l.unit_price);
    return {
      line_total: net,
      line_tax: r2(net * rate),
      account_id: l.account_id,
      tax_code_id: l.tax_code_id,
    };
  });

  const taxTotal = r2(lineCalcs.reduce((s, l) => s + l.line_tax, 0));
  const subtotal = r2(lineCalcs.reduce((s, l) => s + l.line_total, 0));
  const total = r2(subtotal + taxTotal);

  const journalLines: JournalLineInput[] = [];
  const expBy: Record<string, number> = {};
  for (const lc of lineCalcs) {
    const accId = lc.account_id ?? accounts.find((a) => a.code === '6900')?.id;
    if (!accId) return { journal_id: null, error: 'Bill line has no expense account and 6900 fallback missing.' };
    expBy[accId] = r2((expBy[accId] ?? 0) + lc.line_total);
  }
  for (const [accId, amt] of Object.entries(expBy)) {
    journalLines.push({
      account_id: accId,
      debit: amt,
      credit: 0,
      contact_id: bill.contact_id,
    });
  }
  if (taxTotal > 0) {
    if (!vatIn) return { journal_id: null, error: 'VAT input account missing — chart of accounts may be incomplete.' };
    journalLines.push({
      account_id: vatIn.id,
      debit: taxTotal,
      credit: 0,
      contact_id: bill.contact_id,
      description: 'VAT input',
    });
  }
  journalLines.push({
    account_id: ap.id,
    debit: 0,
    credit: total,
    contact_id: bill.contact_id,
    description: `Bill ${bill.supplier_invoice_number ?? bill.internal_ref ?? bill.id.slice(0, 8)}`,
  });

  const result = await postJournal({
    org_id,
    date: bill.issue_date,
    reference: bill.supplier_invoice_number ?? bill.internal_ref ?? null,
    description: `Purchase bill ${bill.supplier_invoice_number ?? ''}`.trim(),
    source: 'bill',
    source_id: bill.id,
    lines: journalLines,
  });
  if (result.error || !result.journal_id) return result;

  const { error: ue } = await supabase
    .from('dodobooks_bills')
    .update({
      subtotal,
      tax_total: taxTotal,
      total,
      status: 'approved',
      journal_id: result.journal_id,
    })
    .eq('id', bill.id);
  if (ue) return { journal_id: null, error: ue.message };

  return result;
}

/**
 * Sales credit note — reverses an invoice's journal at gross amount.
 *   CR  1200 Accounts receivable  (gross)
 *     DR  4xxx revenue account
 *     DR  2100 VAT output
 * Optionally applies to a specific source invoice (reduces its outstanding).
 */
export async function postCreditNoteSales(opts: {
  org_id: string;
  invoice: Invoice;          // the credit-note row (kind='credit_note')
  lines: InvoiceLine[];
  accounts: Account[];
  taxCodes: TaxCode[];
  applies_to_invoice?: Invoice | null;
}): Promise<{ journal_id: string | null; error: string | null }> {
  const { org_id, invoice, lines, accounts, taxCodes, applies_to_invoice } = opts;
  const ar = accounts.find((a) => a.code === '1200');
  const vatOut = accounts.find((a) => a.sars_role === 'vat_output');
  if (!ar) return { journal_id: null, error: 'Missing AR account (1200).' };

  let subtotal = 0, taxTotal = 0;
  const revBy: Record<string, number> = {};
  for (const l of lines) {
    const tc = taxCodes.find((t) => t.id === l.tax_code_id);
    const rate = tc?.rate ?? 0;
    const net = r2(l.qty * l.unit_price);
    subtotal = r2(subtotal + net);
    taxTotal = r2(taxTotal + r2(net * rate));
    const accId = l.account_id ?? accounts.find((a) => a.code === '4000')?.id;
    if (!accId) return { journal_id: null, error: 'Credit-note line has no revenue account.' };
    revBy[accId] = r2((revBy[accId] ?? 0) + net);
  }
  const total = r2(subtotal + taxTotal);

  const journalLines: JournalLineInput[] = [];
  // Debit revenue accounts to reverse income
  for (const [accId, amt] of Object.entries(revBy)) {
    journalLines.push({ account_id: accId, debit: amt, credit: 0, contact_id: invoice.contact_id });
  }
  // Debit VAT output to reverse output VAT (if any)
  if (taxTotal > 0) {
    if (!vatOut) return { journal_id: null, error: 'VAT output account missing.' };
    journalLines.push({ account_id: vatOut.id, debit: taxTotal, credit: 0, contact_id: invoice.contact_id, description: 'VAT output (credit note)' });
  }
  // Credit AR to reduce the customer's debt
  journalLines.push({ account_id: ar.id, debit: 0, credit: total, contact_id: invoice.contact_id, description: `Credit note ${invoice.number}` });

  const result = await postJournal({
    org_id,
    date: invoice.issue_date,
    reference: invoice.number,
    description: `Sales credit note ${invoice.number}`,
    source: 'invoice',
    source_id: invoice.id,
    lines: journalLines,
  });
  if (result.error || !result.journal_id) return result;

  await supabase
    .from('dodobooks_invoices')
    .update({
      subtotal, tax_total: taxTotal, total,
      status: 'sent',
      journal_id: result.journal_id,
    })
    .eq('id', invoice.id);

  // If applied to a source invoice, increase its amount_paid so it nets out.
  if (applies_to_invoice) {
    const newPaid = r2(Number(applies_to_invoice.amount_paid) + total);
    const fullyApplied = newPaid >= Number(applies_to_invoice.total) - 0.005;
    await supabase
      .from('dodobooks_invoices')
      .update({ amount_paid: newPaid, status: fullyApplied ? 'paid' : applies_to_invoice.status })
      .eq('id', applies_to_invoice.id);
  }

  return result;
}

/**
 * Purchase credit note — reverses a bill's journal at gross amount.
 *   DR  2000 Accounts payable     (gross)
 *     CR  expense / asset account
 *     CR  1400 VAT input
 */
export async function postCreditNotePurchase(opts: {
  org_id: string;
  bill: Bill;                // the credit-note row (kind='credit_note')
  lines: BillLine[];
  accounts: Account[];
  taxCodes: TaxCode[];
  applies_to_bill?: Bill | null;
}): Promise<{ journal_id: string | null; error: string | null }> {
  const { org_id, bill, lines, accounts, taxCodes, applies_to_bill } = opts;
  const ap = accounts.find((a) => a.code === '2000');
  const vatIn = accounts.find((a) => a.sars_role === 'vat_input');
  if (!ap) return { journal_id: null, error: 'Missing AP account (2000).' };

  let subtotal = 0, taxTotal = 0;
  const expBy: Record<string, number> = {};
  for (const l of lines) {
    const tc = taxCodes.find((t) => t.id === l.tax_code_id);
    const rate = tc?.rate ?? 0;
    const net = r2(l.qty * l.unit_price);
    subtotal = r2(subtotal + net);
    taxTotal = r2(taxTotal + r2(net * rate));
    const accId = l.account_id ?? accounts.find((a) => a.code === '6900')?.id;
    if (!accId) return { journal_id: null, error: 'Credit-note line has no expense account.' };
    expBy[accId] = r2((expBy[accId] ?? 0) + net);
  }
  const total = r2(subtotal + taxTotal);

  const journalLines: JournalLineInput[] = [];
  journalLines.push({ account_id: ap.id, debit: total, credit: 0, contact_id: bill.contact_id, description: `Supplier credit note` });
  for (const [accId, amt] of Object.entries(expBy)) {
    journalLines.push({ account_id: accId, debit: 0, credit: amt, contact_id: bill.contact_id });
  }
  if (taxTotal > 0) {
    if (!vatIn) return { journal_id: null, error: 'VAT input account missing.' };
    journalLines.push({ account_id: vatIn.id, debit: 0, credit: taxTotal, contact_id: bill.contact_id, description: 'VAT input (credit note)' });
  }

  const result = await postJournal({
    org_id,
    date: bill.issue_date,
    reference: bill.supplier_invoice_number ?? bill.internal_ref ?? null,
    description: `Supplier credit note`,
    source: 'bill',
    source_id: bill.id,
    lines: journalLines,
  });
  if (result.error || !result.journal_id) return result;

  await supabase
    .from('dodobooks_bills')
    .update({ subtotal, tax_total: taxTotal, total, status: 'approved', journal_id: result.journal_id })
    .eq('id', bill.id);

  if (applies_to_bill) {
    const newPaid = r2(Number(applies_to_bill.amount_paid) + total);
    const fullyApplied = newPaid >= Number(applies_to_bill.total) - 0.005;
    await supabase
      .from('dodobooks_bills')
      .update({ amount_paid: newPaid, status: fullyApplied ? 'paid' : applies_to_bill.status })
      .eq('id', applies_to_bill.id);
  }

  return result;
}

/**
 * Record a payment from a customer against a sales invoice, OR a payment
 * we made to a supplier against a purchase bill.
 *   received: DR bank · CR AR
 *   made:     DR AP   · CR bank
 * Updates the parent invoice/bill amount_paid + flips status to 'paid'
 * when the gross total is met. Inserts a row into dodobooks_payments
 * linked to the journal it just created.
 */
export async function postPayment(opts: {
  org_id: string;
  direction: 'received' | 'made';
  contact_id: string;
  invoice?: Invoice | null;
  bill?: Bill | null;
  amount: number;
  date: string;
  bank_account: BankAccount;   // we use its account_id (GL) as the bank leg
  accounts: Account[];
  reference?: string | null;
  notes?: string | null;
}): Promise<{ journal_id: string | null; payment_id: string | null; error: string | null }> {
  const { org_id, direction, contact_id, invoice, bill, amount, date, bank_account, accounts, reference, notes } = opts;
  if (amount <= 0) return { journal_id: null, payment_id: null, error: 'Amount must be positive.' };
  if (direction === 'received' && !invoice) return { journal_id: null, payment_id: null, error: 'Receipt needs an invoice.' };
  if (direction === 'made' && !bill) return { journal_id: null, payment_id: null, error: 'Payment needs a bill.' };

  const ar = accounts.find((a) => a.code === '1200');
  const ap = accounts.find((a) => a.code === '2000');
  if (direction === 'received' && !ar) return { journal_id: null, payment_id: null, error: 'AR account (1200) missing.' };
  if (direction === 'made'     && !ap) return { journal_id: null, payment_id: null, error: 'AP account (2000) missing.' };

  const amt = r2(amount);
  const docLabel = direction === 'received'
    ? `Receipt for ${invoice!.number}`
    : `Payment for ${bill!.supplier_invoice_number ?? bill!.internal_ref ?? bill!.id.slice(0, 8)}`;

  const lines: JournalLineInput[] = direction === 'received'
    ? [
        { account_id: bank_account.account_id, debit: amt, credit: 0, contact_id, description: docLabel },
        { account_id: ar!.id,                  debit: 0,   credit: amt, contact_id, description: docLabel },
      ]
    : [
        { account_id: ap!.id,                  debit: amt, credit: 0, contact_id, description: docLabel },
        { account_id: bank_account.account_id, debit: 0,   credit: amt, contact_id, description: docLabel },
      ];

  const result = await postJournal({
    org_id,
    date,
    reference: reference ?? null,
    description: docLabel,
    source: 'payment',
    source_id: (invoice ?? bill)!.id,
    lines,
  });
  if (result.error || !result.journal_id) return { ...result, payment_id: null };

  // Insert payment row
  const { data: pay, error: pe } = await supabase
    .from('dodobooks_payments')
    .insert({
      org_id, contact_id,
      invoice_id: invoice?.id ?? null,
      bill_id: bill?.id ?? null,
      direction,
      payment_date: date,
      amount: amt,
      currency: (invoice ?? bill)!.currency,
      bank_account_id: bank_account.id,
      reference: reference ?? null,
      notes: notes ?? null,
      journal_id: result.journal_id,
    })
    .select('id')
    .single();
  if (pe || !pay) return { journal_id: result.journal_id, payment_id: null, error: pe?.message ?? 'Failed to record payment row' };

  // Bump parent amount_paid + maybe flip status
  if (direction === 'received' && invoice) {
    const newPaid = r2(Number(invoice.amount_paid) + amt);
    const fullyPaid = newPaid >= Number(invoice.total) - 0.005;
    await supabase
      .from('dodobooks_invoices')
      .update({ amount_paid: newPaid, status: fullyPaid ? 'paid' : invoice.status })
      .eq('id', invoice.id);
  } else if (direction === 'made' && bill) {
    const newPaid = r2(Number(bill.amount_paid) + amt);
    const fullyPaid = newPaid >= Number(bill.total) - 0.005;
    await supabase
      .from('dodobooks_bills')
      .update({ amount_paid: newPaid, status: fullyPaid ? 'paid' : bill.status })
      .eq('id', bill.id);
  }

  return { journal_id: result.journal_id, payment_id: pay.id, error: null };
}

/**
 * Move money between two of the org's own bank accounts.
 *   DR  destination bank GL
 *     CR  source bank GL
 * Posts a single balanced journal of source='manual', description="Transfer".
 */
export async function postBankTransfer(opts: {
  org_id: string;
  from_bank: BankAccount;
  to_bank: BankAccount;
  amount: number;
  date: string;
  reference?: string | null;
  notes?: string | null;
}): Promise<{ journal_id: string | null; error: string | null }> {
  if (opts.from_bank.id === opts.to_bank.id) {
    return { journal_id: null, error: 'Source and destination must differ.' };
  }
  if (opts.amount <= 0) return { journal_id: null, error: 'Amount must be positive.' };
  const amt = r2(opts.amount);
  const label = `Transfer ${opts.from_bank.bank_name} → ${opts.to_bank.bank_name}`;

  return postJournal({
    org_id: opts.org_id,
    date: opts.date,
    reference: opts.reference ?? null,
    description: opts.notes ? `${label}: ${opts.notes}` : label,
    source: 'manual',
    lines: [
      { account_id: opts.to_bank.account_id,   debit: amt, credit: 0, description: label },
      { account_id: opts.from_bank.account_id, debit: 0,   credit: amt, description: label },
    ],
  });
}

/**
 * Manual reconcile of a bank transaction to a single offset account.
 *   amount > 0 (money in):  DR bank, CR offset
 *   amount < 0 (money out): DR offset, CR bank
 */
export async function postBankReconcile(opts: {
  org_id: string;
  bank_account_gl_id: string;   // The GL account_id for the bank account
  bank_txn_id: string;
  date: string;
  amount: number;
  offset_account_id: string;
  contact_id?: string | null;
  tax_code_id?: string | null;
  description: string;
}): Promise<{ journal_id: string | null; error: string | null }> {
  const { amount } = opts;
  const lines: JournalLineInput[] = amount >= 0
    ? [
        { account_id: opts.bank_account_gl_id, debit: r2(amount), credit: 0, description: opts.description, contact_id: opts.contact_id ?? null },
        { account_id: opts.offset_account_id, debit: 0, credit: r2(amount), description: opts.description, contact_id: opts.contact_id ?? null, tax_code_id: opts.tax_code_id ?? null },
      ]
    : [
        { account_id: opts.offset_account_id, debit: r2(-amount), credit: 0, description: opts.description, contact_id: opts.contact_id ?? null, tax_code_id: opts.tax_code_id ?? null },
        { account_id: opts.bank_account_gl_id, debit: 0, credit: r2(-amount), description: opts.description, contact_id: opts.contact_id ?? null },
      ];

  const result = await postJournal({
    org_id: opts.org_id,
    date: opts.date,
    description: opts.description,
    source: 'bank',
    source_id: opts.bank_txn_id,
    lines,
  });
  if (result.error || !result.journal_id) return result;

  await supabase
    .from('dodobooks_bank_transactions')
    .update({ reconciled: true, reconciled_to_journal: result.journal_id })
    .eq('id', opts.bank_txn_id);

  return result;
}
